import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Nav from "../../common/components/Nav";
import Footer from "../../common/components/Footer";
import { fetchAllTeamMembers } from "../../utils/api/team";
import { setAllTeam } from "../../store/actions/actions/team/index.js";
import "./AboutPage.css";

/**
 * Reusable zero-dependency scroll animation wrapper using IntersectionObserver
 */
const ScrollReveal = ({ children, className = "", delayClass = "" }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.15 }
    );

    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`reveal-section ${isVisible ? "is-visible" : ""} ${delayClass} ${className}`}
    >
      {children}
    </div>
  );
};

const AboutPage = () => {
  const [page, setPage] = useState(1);
  const [localTeamMembers, setLocalTeamMembers] = useState([]);
  const dispatch = useDispatch();

  const handleFetchAllTeamMembers = async () => {
    try {
      const data = await fetchAllTeamMembers(page);
      setPage(data.page);
      setLocalTeamMembers(data.allTeams || []);
      dispatch(setAllTeam(data.allTeams || []));
    } catch (error) {
      console.error("Failed to load team members:", error);
    }
  };

  useEffect(() => {
    handleFetchAllTeamMembers();
  }, []);

  const team = localTeamMembers.reduce((acc, member) => {
    acc[member.position] = member;
    return acc;
  }, {});

  const teamPositions = [
    "The Honourable Commissioner",
    "The Permanent Secretary",
    "Director",
  ];

  const teamMembers = teamPositions.map((position) => team[position]);

  const coreFunctions = [
    "Provide advisory role to the State and Local Government on statistical matters.",
    "Research and conduct statistical surveys on the socio-economic activities in the state.",
    "Maintain a comprehensive data bank on the results of the generational coordination of statistical activities in the state.",
    "Maintain liaison with the three tiers of government (federal, state and local government) and multinational organizations on issues pertaining to statistics, research, development, publications and dissemination of statistical data.",
  ];

  return (
    <div
      className="d-flex flex-column min-vh-100 bg-white"
      style={{ fontFamily: "Inter, system-ui, -apple-system, sans-serif" }}
    >
      {/* 1. Navigation Header */}
      <header className="sticky-top bg-white z-3">
        <Nav />
      </header>

      {/* 2. Page Hero Banner */}
      <ScrollReveal>
        <section
          className="text-white text-center py-5 position-relative overflow-hidden"
          style={{
            backgroundColor: "#0B1320",
            backgroundImage:
              "radial-gradient(circle at 50% 30%, rgba(12, 158, 169, 0.2) 0%, rgba(11, 19, 32, 1) 75%)",
          }}
        >
          <div className="container py-lg-4 position-relative" style={{ zIndex: 2 }}>
            <span
              className="badge rounded-pill px-3 py-2 text-uppercase mb-3 fw-semibold"
              style={{
                backgroundColor: "rgba(12, 158, 169, 0.2)",
                color: "#0C9EA9",
                fontSize: "12px",
                letterSpacing: "1px",
              }}
            >
              Ministry of Economic Planning and Budget
            </span>
            <h1 className="display-4 fw-bold mb-3">
              About <span style={{ color: "#0C9EA9" }}>LBS</span>
            </h1>
            <p className="fs-5 text-white-50 max-w-2xl mx-auto fw-light" style={{ maxWidth: "700px" }}>
              Empowering Lagos State through accurate statistical coordination, verified open data, and institutional intelligence.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* 3. Executive Overview / Quip */}
      <ScrollReveal>
        <section className="py-5 bg-white border-bottom">
          <div className="container py-2">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div
                  className="p-4 p-md-5 rounded-4 shadow-sm text-center position-relative"
                  style={{
                    backgroundColor: "#F8FAFC",
                    borderLeft: "6px solid #0C9EA9",
                  }}
                >
                  <p
                    className="fs-5 lh-lg text-dark fw-normal mb-0"
                    style={{ letterSpacing: "-0.2px" }}
                  >
                    The <strong>Lagos Bureau of Statistics (LBS)</strong> is one of the core
                    departments in the <strong>Ministry of Economic Planning and Budget (MEPB)</strong>.
                    The department coordinates statistical activities of the state and advises the
                    state government and its stakeholders on policies, methods, and regulations
                    relating to the development of Lagos State.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* 4. Vision & Mission Cards */}
      <ScrollReveal>
        <section className="py-5 bg-light">
          <div className="container py-2">
            <div className="row g-4">
              <div className="col-md-6">
                <ScrollReveal delayClass="reveal-delay-1">
                  <div className="card border-0 shadow-sm rounded-4 p-4 p-lg-5 bg-white h-100 hover-lift">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-3 mb-3 p-3"
                      style={{
                        backgroundColor: "rgba(12, 158, 169, 0.1)",
                        color: "#0C9EA9",
                        width: "56px",
                        height: "56px",
                        fontSize: "24px",
                      }}
                    >
                      👁️
                    </div>
                    <h3 className="fw-bold text-dark mb-3">Our Vision</h3>
                    <p className="text-secondary fs-6 lh-relaxed mb-0">
                      Ensuring a digitalized, efficient, and timely statistical system for planning,
                      mission policy formulation, and evidence-based decision making.
                    </p>
                  </div>
                </ScrollReveal>
              </div>

              <div className="col-md-6">
                <ScrollReveal delayClass="reveal-delay-2">
                  <div className="card border-0 shadow-sm rounded-4 p-4 p-lg-5 bg-white h-100 hover-lift">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-3 mb-3 p-3"
                      style={{
                        backgroundColor: "rgba(12, 158, 169, 0.1)",
                        color: "#0C9EA9",
                        width: "56px",
                        height: "56px",
                        fontSize: "24px",
                      }}
                    >
                      🎯
                    </div>
                    <h3 className="fw-bold text-dark mb-3">Our Mission</h3>
                    <p className="text-secondary fs-6 lh-relaxed mb-0">
                      To continually be the one-stop shop for qualitative, reliable, and robust data
                      for the continuous development of Lagos State.
                    </p>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* 5. Executive Leadership Team */}
      <ScrollReveal>
        <section className="py-5 bg-white border-top">
          <div className="container py-2">
            <div className="text-center mb-5">
              <h6
                className="text-uppercase fw-bold"
                style={{ color: "#0C9EA9", letterSpacing: "1.5px", fontSize: "12px" }}
              >
                Leadership
              </h6>
              <h2 className="fw-bold text-dark">Meet Our Team</h2>
            </div>

            <div className="row g-4 justify-content-center">
              {teamMembers.map((member, index) => (
                <div className="col-12 col-sm-6 col-lg-4" key={member?.position || index}>
                  <ScrollReveal
                    className="h-100"
                    delayClass={`reveal-delay-${(index % 3) + 1}`}
                  >
                    <article className="team-card">
                      <div className="team-photo">
                        <img
                          src={
                            member?.image
                              ? `${process.env.REACT_APP_ROOT_URL}/uploads/${member.image}`
                              : "/assets/img/placeholder.jpg"
                          }
                          className="team-photo-img"
                          alt={member?.position || "team member"}
                        />
                      </div>
                      <div className="team-card-body">
                        <h5 className="team-name">{member?.name}</h5>
                        <p className="team-role">
                          {member?.position}, <br />
                          {member?.office}
                        </p>
                      </div>
                    </article>
                  </ScrollReveal>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* 6. Core Functions Section */}
      <ScrollReveal>
        <section className="py-5 bg-light border-top">
          <div className="container py-2">
            <div className="text-center mb-5">
              <h6
                className="text-uppercase fw-bold"
                style={{ color: "#0C9EA9", letterSpacing: "1.5px", fontSize: "12px" }}
              >
                Responsibilities
              </h6>
              <h2 className="fw-bold text-dark">
                Core Functions of the Lagos Bureau of Statistics
              </h2>
            </div>

            <div className="row g-4">
              {coreFunctions.map((func, index) => (
                <div className="col-md-6" key={index}>
                  <ScrollReveal delayClass={`reveal-delay-${(index % 2) + 1}`}>
                    <div className="card border-0 shadow-sm rounded-4 p-4 bg-white h-100 d-flex flex-row align-items-start gap-3 hover-shift">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "rgba(12, 158, 169, 0.12)",
                          color: "#0C9EA9",
                          fontSize: "15px",
                        }}
                      >
                        {index + 1}
                      </div>
                      <p className="text-dark fs-6 lh-base m-0 pt-1">{func}</p>
                    </div>
                  </ScrollReveal>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutPage;